import { stateWord } from "@lafayette/domain";
import {
  paymentRefundReasonCodeValues,
  type StaffPaymentListFilter,
  staffPaymentListFilters,
} from "@lafayette/schemas";

import type { Look } from "../requests/labels";

// The words of the Payments UI. A state is never shown by colour alone: every badge has a glyph and a word. The code lists are the
// contract's (`@lafayette/schemas`), and a test fails when a code has no word here.

export const PAYMENT_STATUSES = [
  "DRAFT",
  "READY",
  "PENDING_PAYMENT",
  "PAID",
  "PARTIALLY_REFUNDED",
  "REFUNDED",
  "FAILED",
  "EXPIRED",
  "CANCELLED",
] as const;
export type PaymentStatus = (typeof PAYMENT_STATUSES)[number];

/** How a status looks: a tone and a glyph, always drawn with its word (`stateWord("payment", "staffShort", status)` of `@lafayette/domain`). */
export const statusLook: Readonly<Record<PaymentStatus, Look>> = {
  DRAFT: { tone: "neutral", icon: "edit-02" },
  READY: { tone: "info", icon: "target-02" },
  PENDING_PAYMENT: { tone: "warning", icon: "hourglass" },
  PAID: { tone: "success", icon: "checkmark-circle-02" },
  PARTIALLY_REFUNDED: { tone: "info", icon: "arrow-turn-backward-round" },
  REFUNDED: { tone: "neutral", icon: "arrow-turn-backward-round" },
  FAILED: { tone: "danger", icon: "cancel-circle" },
  EXPIRED: { tone: "neutral", icon: "hourglass-off" },
  CANCELLED: { tone: "neutral", icon: "unavailable" },
};

// A filter that is a payment status takes the staff word of the status from `@lafayette/domain`; the other filters name a queue, not a status.
const staffStatus = (status: PaymentStatus): string => stateWord("payment", "staffShort", status);

export const paymentFilterLabels: Readonly<Record<StaffPaymentListFilter, string>> = {
  READY: staffStatus("READY"),
  PENDING: staffStatus("PENDING_PAYMENT"),
  PAID: staffStatus("PAID"),
  FAILED: staffStatus("FAILED"),
  EXPIRED: staffStatus("EXPIRED"),
  CANCELLED: staffStatus("CANCELLED"),
  REFUND_PENDING: "Refund waiting for the provider",
  REFUNDED: "Refunded (partly or fully)",
  NEEDS_RECONCILIATION: "Needs a reconcile",
};

export const isListFilter = (value: string): value is StaffPaymentListFilter =>
  (staffPaymentListFilters as readonly string[]).includes(value);

export const sourceKindLabels: Readonly<Record<string, string>> = {
  FIXED_PRICE: "Fixed price",
  CONCIERGE_QUOTE: "Concierge quote",
  CATALOG_FIXED_PRICE: "Fixed price",
};

/** What each evidence label means, in words a staff member can act on. FAKE is never launch evidence. */
export const evidenceMeanings = {
  FAKE: "FAKE: made with the fake gateway. No money moved. This is not launch evidence.",
  REAL: "REAL: made with the real payment provider.",
  NONE: "No provider order exists yet, so there is no provider evidence.",
} as const;

export const observationKindLabels: Readonly<Record<string, string>> = {
  AUTHORIZED: "Payment authorized",
  SETTLED: "Payment settled",
  ATTEMPT_FAILED: "One attempt failed",
  FAILED: "Order failed",
  ORDER_EXPIRED: "Order expired",
  REFUND_SETTLED: "Refund settled",
  REFUND_FAILED: "Refund failed",
};

export const decisionLabels: Readonly<Record<string, string>> = {
  APPLIED: "Applied: the payment changed",
  NO_TRANSITION: "Recorded: nothing to change",
  DUPLICATE: "Duplicate: already seen",
  EXCEPTION: "Exception: opened for a person",
};

export const verificationLabels: Readonly<Record<string, string>> = {
  VERIFIED: "Signed webhook",
  API_FETCHED: "Fetched from the provider",
};

export const exceptionKindLabels: Readonly<Record<string, string>> = {
  DUPLICATE_CONFLICT: "Same event, different content",
  UNKNOWN_ORDER: "An order Lafayette does not know",
  ORDER_MISMATCH: "The order does not match the payment",
  CUSTOMER_MISMATCH: "The customer does not match",
  AMOUNT_MISMATCH: "The amount does not match",
  CURRENCY_MISMATCH: "The currency does not match",
  LATE_SETTLEMENT: "Paid after the payment ended",
  REQUEST_WITHDRAWN: "Paid after the request ended",
  UNEXPECTED_STATE: "Unexpected for this state",
  UNKNOWN_REFUND: "A refund Lafayette does not know",
  REFUND_MISMATCH: "The refund does not match",
};

export const resolutionLabels: Readonly<Record<string, string>> = {
  REFUNDED_OUT_OF_BAND: "Refunded outside Lafayette",
  CONFIRMED_NO_MONEY_MOVED: "Confirmed: no money moved",
  ADOPTED_REFUND: "Adopted the refund",
  DUPLICATE_IGNORED: "Duplicate ignored",
  OTHER: "Other",
};

export const refundStatusLabels: Readonly<Record<string, string>> = {
  REQUESTED: "Waiting for the provider",
  SETTLED: "Settled",
  FAILED: "Failed",
};

export const refundReasonLabels: Readonly<Record<string, string>> = {
  CUSTOMER_REQUEST: "The customer asked",
  SUPPLIER_UNAVAILABLE: "The supplier is not available",
  SERVICE_RECOVERY: "Service recovery",
  DUPLICATE_PAYMENT: "Duplicate payment",
  OTHER: "Other",
};

export const refundReasonOptions = paymentRefundReasonCodeValues.map((code) => ({
  value: code,
  label: refundReasonLabels[code] ?? code,
}));

export const providerCallKindLabels: Readonly<Record<string, string>> = {
  CREATE_ORDER: "Create the provider order",
  CREATE_REFUND: "Send a refund",
};

export const providerCallStateLabels: Readonly<Record<string, string>> = {
  CALLING: "In flight",
  AMBIGUOUS: "No clear answer",
  SUCCEEDED: "Succeeded",
  REJECTED: "Rejected",
};

export const errorClassLabels: Readonly<Record<string, string>> = {
  RETRYABLE: "can be retried",
  AMBIGUOUS: "may have happened",
  TERMINAL: "will not work again",
};

export const runScopeLabels: Readonly<Record<string, string>> = {
  PAYMENT: "Reconcile one payment",
  WINDOW: "Reconcile a time window",
  EXPIRY_SWEEP: "Expiry sweep",
};

export const runStateLabels: Readonly<Record<string, string>> = {
  RUNNING: "Running",
  DONE: "Done",
  FAILED: "Failed",
};

export const transitionEvidenceLabels: Readonly<Record<string, string>> = {
  CUSTOMER_COMMAND: "Customer action",
  STAFF_COMMAND: "Staff action",
  SOURCE_VERIFIED: "Source checked",
  PROVIDER_ORDER: "Provider order recorded",
  PROVIDER_OBSERVATION: "Provider evidence",
  PROVIDER_STATUS_CHECK: "Provider status check",
  EXPIRY_CLOCK: "Clock reached the expiry",
  REFUND_OBSERVATION: "Refund evidence",
};

export const quoteStatusLabels: Readonly<Record<string, string>> = {
  APPROVED: "Approved",
  WITHDRAWN: "Replaced or withdrawn",
};

export const supplierEvidenceLabels: Readonly<Record<string, string>> = {
  SUPPLIER_EMAIL: "Supplier e-mail",
  SUPPLIER_PORTAL: "Supplier portal",
  SUPPLIER_CALL: "Supplier call",
  SUPPLIER_DOCUMENT: "Supplier document",
};

export const supplierEvidenceOptions = Object.entries(supplierEvidenceLabels).map(
  ([value, label]) => ({ value, label }),
);

export const approvalBasisLabels: Readonly<Record<string, string>> = {
  OWNER: "the request owner",
  SUPERVISOR: "a supervisor",
};

/** How long a new quote stays valid. The console never asks for a clock time: the server clock and a number of days decide it. */
export const quoteValidityOptions = [
  { value: "1", label: "1 day" },
  { value: "3", label: "3 days" },
  { value: "7", label: "7 days" },
  { value: "14", label: "14 days" },
  { value: "30", label: "30 days (the longest allowed)" },
] as const;

export const label = (table: Readonly<Record<string, string>>, code: string | null): string =>
  code === null ? "—" : (table[code] ?? code);
